import { create } from 'zustand'

type TabValue = 'wards' | 'directions' | 'comparison'
export type ViewMode = '2D' | '3D'

interface UIState {
  // Panel state
  isPanelOpen: boolean
  panelWidth: number
  activeTab: TabValue

  // Mobile detection
  isMobile: boolean
  isTablet: boolean

  // Ward info display
  showWardInfo: boolean
  wardInfoPosition: { x: number; y: number } | null

  // Loading states
  isMapLoading: boolean
  isDataLoading: boolean

  // Map view mode
  viewMode: ViewMode
  isViewSwitching: boolean
  webGL2Supported: boolean

  // Actions
  togglePanel: () => void
  setPanelWidth: (width: number) => void
  setActiveTab: (tab: TabValue) => void
  setMobile: (isMobile: boolean) => void
  setTablet: (isTablet: boolean) => void
  setShowWardInfo: (show: boolean) => void
  setWardInfoPosition: (position: { x: number; y: number } | null) => void
  setMapLoading: (loading: boolean) => void
  setDataLoading: (loading: boolean) => void
  setViewMode: (mode: ViewMode) => void
  setViewSwitching: (switching: boolean) => void
  setWebGL2Supported: (supported: boolean) => void
}

export const useUIStore = create<UIState>((set) => {
  // Check WebGL2 support - moved inside to avoid module-level execution
  const checkWebGL2Support = (): boolean => {
    if (typeof window === 'undefined') return false
    try {
      const canvas = document.createElement('canvas')
      const gl = canvas.getContext('webgl2')
      return !!gl
    } catch {
      return false
    }
  }

  // Helper to get persisted view mode - moved inside to avoid module-level execution
  const getPersistedViewMode = (): ViewMode => {
    if (typeof window === 'undefined') return '2D'
    try {
      const saved = localStorage.getItem('ward-app-view-mode')
      if (saved === '3D' && checkWebGL2Support()) {
        return '3D'
      }
    } catch {
      // Ignore localStorage errors
    }
    return '2D'
  }

  return {
    // State
    isPanelOpen: true,
    panelWidth: 360,
    activeTab: 'wards',
    isMobile: false,
    isTablet: false,
    showWardInfo: false,
    wardInfoPosition: null,
    isMapLoading: false,
    isDataLoading: false,
    viewMode: getPersistedViewMode(),
    isViewSwitching: false,
    webGL2Supported: checkWebGL2Support(),

    // Actions
    togglePanel: () => set((state) => ({ isPanelOpen: !state.isPanelOpen })),

    setPanelWidth: (width) => set({ panelWidth: width }),

    setActiveTab: (tab) => set({ activeTab: tab }),

    setMobile: (isMobile) => set({ isMobile }),

    setTablet: (isTablet) => set({ isTablet }),

    setShowWardInfo: (show) => set({ showWardInfo: show }),

    setWardInfoPosition: (position) => set({ wardInfoPosition: position }),

    setMapLoading: (loading) => set({ isMapLoading: loading }),

    setDataLoading: (loading) => set({ isDataLoading: loading }),

    setViewMode: (mode) => {
      set({ viewMode: mode })
      try {
        // TODO: investigate why the map does not respect it when first load. Disable for now.
        // localStorage.setItem('ward-app-view-mode', mode)
      } catch {
        // Ignore localStorage errors
      }
    },

    setViewSwitching: (switching) => set({ isViewSwitching: switching }),

    setWebGL2Supported: (supported) => set({ webGL2Supported: supported }),
  }
})
